import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, DeviceMobile } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';

// Phone input + "Send OTP" + code entry. Calls onVerified({ phone, token }) when Twilio approves the code.
export function PhoneOtpField({ phone, onPhoneChange, enabled, verified, onVerified, onReset }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/otp/send', { phone });
      setSent(true); setCooldown(30); setCode('');
      toast.success(`OTP sent to ${data.phone}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not send OTP');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/otp/verify', { phone, code });
      onVerified({ phone: data.phone, token: data.verification_token });
      toast.success('Mobile number verified');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Incorrect OTP');
    } finally { setBusy(false); }
  };

  const changePhone = (e) => {
    onPhoneChange(e);
    if (verified || sent) { setSent(false); setCode(''); onReset?.(); }
  };

  const phoneOk = phone.replace(/\D/g, '').length >= 10;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={phone} onChange={changePhone} placeholder="9999999999" inputMode="tel" required={enabled} readOnly={verified}
          className={verified ? 'border-emerald-400 bg-emerald-50/40' : ''} data-testid="signup-phone" />
        {enabled && !verified && (
          <Button type="button" variant="outline" onClick={send} disabled={!phoneOk || busy || cooldown > 0}
            className="shrink-0 h-10 rounded-lg text-xs font-semibold" data-testid="signup-send-otp">
            <DeviceMobile size={14} className="mr-1" /> {cooldown > 0 ? `Resend in ${cooldown}s` : sent ? 'Resend OTP' : 'Send OTP'}
          </Button>
        )}
        {verified && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-testid="signup-phone-verified">
            <CheckCircle size={16} weight="fill" /> Verified
          </span>
        )}
      </div>
      {enabled && sent && !verified && (
        <div className="flex gap-2" data-testid="signup-otp-row">
          <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP"
            inputMode="numeric" autoFocus className="font-mono tracking-[0.3em]" data-testid="signup-otp-input" />
          <Button type="button" onClick={verify} disabled={code.length < 4 || busy}
            className="shrink-0 h-10 rounded-lg bg-[#0A0A0A] text-white hover:bg-[#27272A] text-xs font-semibold" data-testid="signup-verify-otp">
            {busy ? 'Checking…' : 'Verify'}
          </Button>
        </div>
      )}
    </div>
  );
}
